import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Input,
  Badge,
  Skeleton,
  Modal,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import {
  usePlatformUser,
  useUpdatePlatformUser,
  useDeactivatePlatformUser,
  useReactivatePlatformUser,
  PlatformUserStatusBadge,
} from '@/features/platform-users';
import {
  PLATFORM_ROLE_BADGE_VARIANT,
  PLATFORM_ROLE_DESCRIPTION,
  PLATFORM_ROLE_LABEL,
  type PlatformRole,
  type UpdatePlatformUserPayload,
} from '@/types/platform-user';
import { useAuthStore } from '@/stores/use-auth-store';
import { ROLE_LEVELS } from '@/types/user';

const E164_REGEX = /^\+[1-9]\d{6,14}$/;

const ROLE_OPTIONS: { value: PlatformRole; label: string }[] = [
  { value: 'SUPER_ADMIN', label: 'Super Admin' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'MODERATOR', label: 'Moderator' },
];

const ROLE_TONE: Record<PlatformRole, string> = {
  SUPER_ADMIN: 'from-danger-500 to-rose-600',
  ADMIN: 'from-brand-500 to-indigo-600',
  MODERATOR: 'from-warning-500 to-amber-600',
};

// ─── Icons ──────────────────────────────────────────────────────────────────

function ArrowLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );
}

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  );
}

function BanIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
    </svg>
  );
}

function UndoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v6h6M20 20v-6h-6M20 9A8 8 0 006.3 6.3L4 8.5m0 6.5a8 8 0 0013.7 2.7L20 15.5" />
    </svg>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');
}

function formatDateTime(iso: string | undefined): string {
  if (!iso) return '\u2014';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function InfoRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | React.ReactNode | null | undefined;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg px-3 py-2.5 odd:bg-neutral-50/60 dark:odd:bg-neutral-800/40">
      <span className="text-sm text-neutral-500 dark:text-neutral-400">{label}</span>
      <span
        className={`text-right text-sm font-medium text-neutral-900 dark:text-white ${
          mono ? 'font-mono tabular-nums tracking-wide' : ''
        }`}
      >
        {value || '\u2014'}
      </span>
    </div>
  );
}

// ─── Capability Matrix ──────────────────────────────────────────────────────

interface Capability {
  label: string;
  /** Minimum role level required (PLATFORM_ROLE_LEVELS) */
  minLevel: number;
}

const CAPABILITIES: Capability[] = [
  { label: 'View platform users', minLevel: 60 },
  { label: 'View tenants & global data', minLevel: 60 },
  { label: 'Manage tenant operations', minLevel: 80 },
  { label: 'Create platform user', minLevel: 100 },
  { label: 'Edit role / Deactivate / Reactivate platform user', minLevel: 100 },
];

const MATRIX_ROLES: PlatformRole[] = ['MODERATOR', 'ADMIN', 'SUPER_ADMIN'];

const ROLE_LEVEL: Record<PlatformRole, number> = {
  MODERATOR: 60,
  ADMIN: 80,
  SUPER_ADMIN: 100,
};

function CheckGlyph({ on }: { on: boolean }) {
  if (on) {
    return (
      <svg className="mx-auto h-4 w-4 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    );
  }
  return <span className="text-neutral-300 dark:text-neutral-600">—</span>;
}

// ─── Content ────────────────────────────────────────────────────────────────

function PlatformUserDetailContent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const profile = useAuthStore((s) => s.profile);

  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const isSuperAdmin = roleLevel >= ROLE_LEVELS.SUPER_ADMIN;
  const isSelf = profile?.id === id;

  const { data: user, isLoading, error } = usePlatformUser(id);
  const updateMutation = useUpdatePlatformUser();
  const deactivateMutation = useDeactivatePlatformUser();
  const reactivateMutation = useReactivatePlatformUser();

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    phone: '',
    role: 'ADMIN' as PlatformRole,
  });
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (error) toast({ title: (error as Error).message, variant: 'error' });
  }, [error, toast]);

  function openEditModal() {
    if (!user) return;
    setEditForm({
      name: user.name,
      phone: user.phone ?? '',
      role: user.role,
    });
    setEditErrors({});
    setShowEditModal(true);
  }

  function validateEdit(): boolean {
    const errs: Record<string, string> = {};
    if (!editForm.name.trim()) errs.name = 'Name is required';
    else if (editForm.name.trim().length < 2) errs.name = 'Min 2 characters';
    if (editForm.phone && !E164_REGEX.test(editForm.phone)) {
      errs.phone = 'Enter a valid phone number';
    }
    setEditErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleEdit() {
    if (!id || !user || !validateEdit()) return;
    const payload: UpdatePlatformUserPayload = {};
    if (editForm.name.trim() !== user.name) payload.name = editForm.name.trim();
    if (editForm.phone !== (user.phone ?? '')) payload.phone = editForm.phone || undefined;
    if (editForm.role !== user.role) payload.role = editForm.role;

    if (Object.keys(payload).length === 0) {
      setShowEditModal(false);
      return;
    }

    updateMutation.mutate(
      { id, payload },
      {
        onSuccess: () => {
          toast({ title: 'Platform user updated', variant: 'success' });
          setShowEditModal(false);
        },
        onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
      },
    );
  }

  function handleDeactivate() {
    if (!id) return;
    deactivateMutation.mutate(id, {
      onSuccess: () => {
        toast({ title: 'Platform user deactivated', variant: 'success' });
        setShowDeactivateModal(false);
      },
      onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
    });
  }

  function handleReactivate() {
    if (!id) return;
    reactivateMutation.mutate(id, {
      onSuccess: () => {
        toast({ title: 'Platform user reactivated', variant: 'success' });
        setShowReactivateModal(false);
      },
      onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
    });
  }

  // ── Loading ───────────────────────────────────────────────────────────────

  if (isLoading && !user) {
    return (
      <>
        <Skeleton variant="rect" height={96} className="mb-6 rounded-2xl" />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Skeleton variant="rect" height={420} className="rounded-xl" />
          </div>
          <Skeleton variant="rect" height={280} className="rounded-xl" />
        </div>
      </>
    );
  }

  if (!user) {
    return (
      <div className="py-20 text-center">
        <p className="text-neutral-500 dark:text-neutral-400">Platform user not found</p>
        <Button variant="secondary" onClick={() => navigate('/platform-users')} className="mt-4">
          Back to Platform Users
        </Button>
      </div>
    );
  }

  const deactivateDisabled = !isSuperAdmin || isSelf || !user.is_active;
  const deactivateTooltip = !isSuperAdmin
    ? 'Only Super Admins can deactivate platform users'
    : isSelf
      ? 'You cannot deactivate your own account'
      : !user.is_active
        ? 'Already deactivated'
        : '';

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="relative mb-6 overflow-hidden rounded-2xl bg-gradient-to-r from-brand-600 via-indigo-600 to-violet-600 p-6 text-white shadow-[0_20px_60px_-20px_rgba(67,56,202,0.55)]">
        <div className="pointer-events-none absolute -right-24 -top-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => navigate('/platform-users')}
              className="rounded-lg p-2 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              aria-label="Back"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br ${ROLE_TONE[user.role]} text-lg font-bold text-white shadow-lg ring-2 ring-white/30`}>
              {initialsOf(user.name)}
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-bold text-white">{user.name}</h1>
                <Badge variant={PLATFORM_ROLE_BADGE_VARIANT[user.role]} size="sm">
                  <span className="flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-current" />
                    {PLATFORM_ROLE_LABEL[user.role]}
                  </span>
                </Badge>
                <PlatformUserStatusBadge isActive={user.is_active} />
                {isSelf && (
                  <Badge variant="info" size="sm">
                    <span className="flex items-center gap-1.5">You</span>
                  </Badge>
                )}
              </div>
              <p className="mt-1 font-mono text-sm text-indigo-100">{user.email}</p>
            </div>
          </div>
          {isSuperAdmin && (
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                onClick={openEditModal}
                className="border-white/30 bg-white/20 text-white hover:bg-white/30"
              >
                <PencilIcon className="mr-1.5 h-4 w-4" />
                Edit
              </Button>
              {user.is_active ? (
                <Button
                  variant="danger"
                  onClick={() => setShowDeactivateModal(true)}
                  disabled={deactivateDisabled}
                  title={deactivateTooltip || undefined}
                  className="bg-white/95 text-danger-600 hover:bg-white disabled:opacity-50"
                >
                  <BanIcon className="mr-1.5 h-4 w-4" />
                  Deactivate
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={() => setShowReactivateModal(true)}
                  className="bg-white/95 text-success-700 hover:bg-white"
                >
                  <UndoIcon className="mr-1.5 h-4 w-4" />
                  Reactivate
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Two-column info grid */}
      <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Profile */}
        <div className="space-y-6 rounded-2xl bg-white/80 p-6 shadow-md backdrop-blur-sm lg:col-span-2 dark:bg-neutral-900/80">
          <section>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              Profile
            </h3>
            <InfoRow label="Full Name" value={user.name} />
            <InfoRow label="Email" value={user.email} mono />
            <InfoRow label="Phone" value={user.phone} mono />
          </section>

          <section>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              Access
            </h3>
            <InfoRow
              label="Platform Role"
              value={
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full bg-gradient-to-br ${ROLE_TONE[user.role]}`} />
                  <span>{PLATFORM_ROLE_LABEL[user.role]}</span>
                </div>
              }
            />
            <InfoRow label="Scope" value="All tenants (platform-wide)" />
            <InfoRow
              label="Status"
              value={<PlatformUserStatusBadge isActive={user.is_active} />}
            />
          </section>

          <section>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              Activity
            </h3>
            <InfoRow label="Last Login" value={formatDateTime(user.last_login_at)} mono />
            <InfoRow label="Created" value={formatDateTime(user.created_at)} mono />
            <InfoRow label="Last Updated" value={formatDateTime(user.updated_at)} mono />
            <InfoRow label="User ID" value={<span className="font-mono text-xs">{user.id}</span>} />
          </section>
        </div>

        {/* Capability matrix card */}
        <div className="space-y-4">
          <div className="rounded-2xl bg-white/80 p-5 shadow-md backdrop-blur-sm dark:bg-neutral-900/80">
            <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              Role Capabilities
            </h3>

            {/* Active role banner */}
            <div
              className={`mb-4 rounded-xl bg-gradient-to-br ${ROLE_TONE[user.role]} p-4 text-white shadow-sm`}
            >
              <p className="text-sm font-semibold">{PLATFORM_ROLE_LABEL[user.role]}</p>
              <p className="mt-1 text-xs leading-snug text-white/90">
                {PLATFORM_ROLE_DESCRIPTION[user.role]}
              </p>
            </div>

            {/* Matrix */}
            <div className="overflow-hidden rounded-xl bg-neutral-50/60 dark:bg-neutral-800/40">
              <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-2 px-3 pt-3 pb-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
                  Capability
                </span>
                {MATRIX_ROLES.map((r) => {
                  const active = r === user.role;
                  return (
                    <span
                      key={r}
                      className={`min-w-[3rem] rounded-md px-2 py-1 text-center text-[10px] font-semibold uppercase tracking-wider ${
                        active
                          ? `bg-gradient-to-br ${ROLE_TONE[r]} text-white shadow-sm`
                          : 'text-neutral-400'
                      }`}
                    >
                      {r === 'SUPER_ADMIN' ? 'Super' : r === 'MODERATOR' ? 'Mod' : 'Admin'}
                    </span>
                  );
                })}
              </div>
              {CAPABILITIES.map((cap, idx) => (
                <div
                  key={cap.label}
                  className={`grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-2 px-3 py-2 ${
                    idx % 2 === 0 ? 'bg-white/40 dark:bg-neutral-900/30' : ''
                  }`}
                >
                  <span className="text-xs text-neutral-700 dark:text-neutral-300">{cap.label}</span>
                  {MATRIX_ROLES.map((r) => (
                    <span key={r} className="min-w-[3rem] text-center">
                      <CheckGlyph on={ROLE_LEVEL[r] >= cap.minLevel} />
                    </span>
                  ))}
                </div>
              ))}
            </div>

            {/* Footer rules */}
            <div className="mt-4 space-y-2 text-xs text-neutral-600 dark:text-neutral-400">
              <div className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" />
                <p>Platform users bypass tenant Row-Level Security across all tenants.</p>
              </div>
              <div className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-danger-500" />
                <p>Deactivation invalidates all active sessions immediately.</p>
              </div>
              <div className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-success-500" />
                <p>A Super Admin can reactivate a deactivated account from this page.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <Modal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Platform User"
        variant="wide"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleEdit} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        }
      >
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Full Name <span className="text-danger-500">*</span>
              </label>
              <Input
                value={editForm.name}
                onChange={(v) => setEditForm((f) => ({ ...f, name: v }))}
              />
              {editErrors.name && <p className="mt-1 text-xs text-danger-500">{editErrors.name}</p>}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Phone
              </label>
              <Input
                // no-tenant-dial: Orio360 platform staff, not a tenant user — there is no
                // owning tenant whose country this should follow.
                variant="phone"
                value={editForm.phone}
                onChange={(v) => setEditForm((f) => ({ ...f, phone: v }))}
                placeholder="98765 43210"
                className="font-mono tabular-nums"
              />
              {editErrors.phone && <p className="mt-1 text-xs text-danger-500">{editErrors.phone}</p>}
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Platform Role
            </label>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
              {ROLE_OPTIONS.map((opt) => {
                const selected = editForm.role === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setEditForm((f) => ({ ...f, role: opt.value }))}
                    aria-pressed={selected}
                    className={`relative overflow-hidden rounded-lg p-3 text-left transition-all ${
                      selected
                        ? 'bg-white shadow-md ring-2 ring-brand-500 dark:bg-neutral-800'
                        : 'bg-neutral-50 hover:bg-neutral-100 dark:bg-neutral-800/50 dark:hover:bg-neutral-800'
                    }`}
                  >
                    <div className="mb-1 flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full bg-gradient-to-br ${ROLE_TONE[opt.value]}`} />
                      <span className="text-sm font-semibold text-neutral-900 dark:text-white">
                        {opt.label}
                      </span>
                    </div>
                    <p className="text-xs leading-snug text-neutral-500 dark:text-neutral-400">
                      {PLATFORM_ROLE_DESCRIPTION[opt.value]}
                    </p>
                  </button>
                );
              })}
            </div>
            {isSelf && editForm.role !== user.role && (
              <p className="mt-2 text-xs text-warning-600">
                You are changing your own role. You will need to sign in again for the change to take effect.
              </p>
            )}
          </div>
        </div>
      </Modal>

      {/* Deactivate Modal */}
      <Modal
        open={showDeactivateModal}
        onClose={() => setShowDeactivateModal(false)}
        title="Deactivate Platform User"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowDeactivateModal(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeactivate}
              disabled={deactivateMutation.isPending}
            >
              {deactivateMutation.isPending ? 'Deactivating...' : `Deactivate ${user.name}`}
            </Button>
          </div>
        }
      >
        <div className="space-y-3 text-sm text-neutral-600 dark:text-neutral-400">
          <p>
            Deactivate <strong className="text-neutral-900 dark:text-white">{user.name}</strong> (
            <span className="font-mono">{user.email}</span>)?
          </p>
          <p>
            This immediately revokes their access and invalidates all active sessions. Historical audit data
            is preserved. The account can be reactivated later by a Super Admin.
          </p>
        </div>
      </Modal>

      {/* Reactivate Modal */}
      <Modal
        open={showReactivateModal}
        onClose={() => setShowReactivateModal(false)}
        title="Reactivate Platform User"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowReactivateModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleReactivate}
              disabled={reactivateMutation.isPending}
            >
              {reactivateMutation.isPending ? 'Reactivating...' : `Reactivate ${user.name}`}
            </Button>
          </div>
        }
      >
        <div className="space-y-3 text-sm text-neutral-600 dark:text-neutral-400">
          <p>
            Reactivate <strong className="text-neutral-900 dark:text-white">{user.name}</strong> (
            <span className="font-mono">{user.email}</span>)?
          </p>
          <p>
            They regain {PLATFORM_ROLE_LABEL[user.role]} access immediately and can sign in again with
            their existing credentials. The role is unchanged &mdash; edit it first if it should differ.
          </p>
        </div>
      </Modal>
    </div>
  );
}

// ─── Page export ────────────────────────────────────────────────────────────

export function PlatformUserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: user } = usePlatformUser(id);

  return (
    <PageContainer
      title={user?.name ?? 'Platform User'}
      breadcrumbs={[
        { label: 'Platform', href: '/dashboard' },
        { label: 'Platform Users', href: '/platform-users' },
        { label: user?.name ?? 'Detail' },
      ]}
    >
      <ErrorBoundary>
        <PlatformUserDetailContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
